import { useCallback, useState } from "react";

import {
  type SportsbookTranslation,
  type SportsbookTranslationLocale,
  useDeleteSportsbookTranslation,
  useSportsbookTranslation,
  useUpdateSportsbookTranslation,
} from "../../../../queries/sportsbook-fm/sportsbookTranslations";
import { matchActiveLanguageTag } from "./activeLanguage";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { formatString } from "../../../../utils/constants/stringUtils";

export interface UseSportsbookTranslationEditorOptions {
  entityKey: string | undefined;
  enabled: boolean;
  initialData?: SportsbookTranslation;
}

export interface SportsbookTranslationEditor {
  enabled: boolean;
  entityKey: string | undefined;
  query: ReturnType<typeof useSportsbookTranslation>;
  data: ReturnType<typeof useSportsbookTranslation>["data"];
  locales: SportsbookTranslationLocale[];
  selectedLocale: string | null;
  selectedTranslation: SportsbookTranslationLocale | undefined;
  selectedValue: string;
  selectedIsDirty: boolean;
  drafts: Record<string, string>;
  dirtyTranslationValues: Record<string, string>;
  hasDirtyTranslations: boolean;
  hasEmptyTranslationDraft: boolean;
  isMutating: boolean;
  mutationError: string | null;
  selectLocale: (locale: string) => void;
  changeDraft: (value: string) => void;
  resetDraft: () => void;
  confirmStale: () => Promise<void>;
  discardOverride: () => Promise<void>;
  saveTranslations: () => Promise<boolean>;
  showUnsavedChangesError: () => void;
  reset: () => void;
}

const getEffectiveTranslation = (
  locale: SportsbookTranslationLocale,
): string => locale.override ?? locale.machine ?? "";

/**
 * The base language is edited in the name field, so it never gets its own
 * chip. Matched on the primary subtag so an `en-US` base also covers `en`.
 */
const isBaseLanguage = (locale: string, baseLocale: string): boolean => {
  const tag = locale.toLowerCase();
  const base = baseLocale.toLowerCase();
  return !!base && (tag === base || tag === base.split("-")[0]);
};

/**
 * Opens on the language the admin is already viewing PAM in — that is the
 * translation they came to check.
 */
const findActiveLanguageLocale = (
  locales: SportsbookTranslationLocale[],
): SportsbookTranslationLocale | undefined => {
  const active = matchActiveLanguageTag(locales.map((locale) => locale.locale));
  return active
    ? locales.find((locale) => locale.locale === active)
    : undefined;
};

export function useSportsbookTranslationEditor({
  entityKey,
  enabled,
  initialData,
}: UseSportsbookTranslationEditorOptions): SportsbookTranslationEditor {
  const activeEntityKey = enabled && entityKey ? entityKey : undefined;
  const activeInitialData = enabled ? initialData : undefined;
  // `initialData` means the caller already has the payload, so the fetch is
  // skipped. The key is still passed so translation mutations write into the
  // cache this editor reads from.
  const query = useSportsbookTranslation(activeEntityKey, {
    initialData: activeInitialData,
    enabled: activeInitialData === undefined,
  });
  const updateMutation = useUpdateSportsbookTranslation();
  const deleteMutation = useDeleteSportsbookTranslation();
  const [selectedLocaleState, setSelectedLocaleState] = useState<string | null>(
    null,
  );
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // A seeded payload comes from the caller's list row, which is updated in
  // place after a save. `initialData` only fills an empty cache entry, so
  // reading the query back would replay the payload from the first open.
  const data = activeInitialData ?? query.data;
  const locales = (data?.locales ?? []).filter(
    (locale) => !isBaseLanguage(locale.locale, data?.base.locale ?? ""),
  );
  const selectedTranslation =
    locales.find((locale) => locale.locale === selectedLocaleState) ??
    findActiveLanguageLocale(locales) ??
    locales[0];
  const selectedLocale = selectedTranslation?.locale ?? null;
  const selectedValue = selectedTranslation
    ? drafts[selectedTranslation.locale] ??
      getEffectiveTranslation(selectedTranslation)
    : "";
  const selectedIsDirty = selectedLocale
    ? Object.prototype.hasOwnProperty.call(drafts, selectedLocale)
    : false;
  const dirtyLocales = locales.filter((locale) =>
    Object.prototype.hasOwnProperty.call(drafts, locale.locale),
  );
  const hasDirtyTranslations = dirtyLocales.length > 0;
  const dirtyTranslationValues = Object.fromEntries(
    dirtyLocales.map((locale) => [locale.locale, drafts[locale.locale].trim()]),
  );
  const hasEmptyTranslationDraft = dirtyLocales.some(
    (locale) => !drafts[locale.locale].trim(),
  );
  const isMutating =
    isSaving || updateMutation.isPending || deleteMutation.isPending;

  const removeDraft = (locale: string) => {
    setDrafts((current) => {
      const next = { ...current };
      delete next[locale];
      return next;
    });
  };

  const updateTranslation = async (locale: string, value: string) => {
    if (!activeEntityKey) return;
    await updateMutation.mutateAsync({
      entityKey: activeEntityKey,
      locale,
      value,
    });
    removeDraft(locale);
  };

  const deleteTranslation = async (locale: string) => {
    if (!activeEntityKey) return;
    await deleteMutation.mutateAsync({ entityKey: activeEntityKey, locale });
    removeDraft(locale);
  };

  const selectLocale = (locale: string) => {
    setSelectedLocaleState(locale);
    setMutationError(null);
  };

  const changeDraft = (value: string) => {
    if (!selectedTranslation) return;
    setMutationError(null);
    setDrafts((current) => {
      const next = { ...current };
      if (value === getEffectiveTranslation(selectedTranslation)) {
        delete next[selectedTranslation.locale];
      } else {
        next[selectedTranslation.locale] = value;
      }
      return next;
    });
  };

  const resetDraft = () => {
    if (!selectedTranslation) return;
    setMutationError(null);
    removeDraft(selectedTranslation.locale);
  };

  const confirmStale = async () => {
    if (!selectedTranslation?.override) return;
    setMutationError(null);
    try {
      await updateTranslation(
        selectedTranslation.locale,
        selectedTranslation.override,
      );
    } catch {
      setMutationError(
        formatString(AppStrings.SPORTSBOOK_FM_SAVE_TRANSLATION_ERROR, {
          locale: selectedTranslation.locale,
        }),
      );
    }
  };

  const discardOverride = async () => {
    if (!selectedTranslation || selectedIsDirty) return;
    setMutationError(null);
    try {
      await deleteTranslation(selectedTranslation.locale);
    } catch {
      setMutationError(
        formatString(AppStrings.SPORTSBOOK_FM_DELETE_TRANSLATION_ERROR, {
          locale: selectedTranslation.locale,
        }),
      );
    }
  };

  const saveTranslations = async (): Promise<boolean> => {
    if (!hasDirtyTranslations) return true;
    setMutationError(null);
    setIsSaving(true);
    let savingLocale = "";
    try {
      for (const locale of dirtyLocales) {
        savingLocale = locale.locale;
        await updateTranslation(locale.locale, drafts[locale.locale].trim());
      }
      return true;
    } catch {
      setMutationError(
        formatString(AppStrings.SPORTSBOOK_FM_SAVE_TRANSLATION_ERROR, {
          locale: savingLocale,
        }),
      );
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const showUnsavedChangesError = () => {
    setMutationError(AppStrings.SPORTSBOOK_FM_UNSAVED_TRANSLATIONS);
  };

  const reset = useCallback(() => {
    setSelectedLocaleState(null);
    setDrafts({});
    setMutationError(null);
  }, []);

  return {
    enabled,
    entityKey: activeEntityKey,
    query,
    data,
    locales,
    selectedLocale,
    selectedTranslation,
    selectedValue,
    selectedIsDirty,
    drafts,
    dirtyTranslationValues,
    hasDirtyTranslations,
    hasEmptyTranslationDraft,
    isMutating,
    mutationError,
    selectLocale,
    changeDraft,
    resetDraft,
    confirmStale,
    discardOverride,
    saveTranslations,
    showUnsavedChangesError,
    reset,
  };
}
